import { Mail, Phone, MapPin, Calendar, Sparkles, ArrowRight, ChevronDown } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import ContactCTA from "@/components/ContactCTA";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

// Animated background component
const AnimatedBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      {/* Animated grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
      {/* Floating orbs */}
      <motion.div
        className="absolute top-20 right-20 w-96 h-96 bg-gold/20 rounded-full blur-3xl"
        animate={{
          x: [0, 100, 0],
          y: [0, -50, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, -50, 0],
          y: [0, 100, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  );
};

const ContactPage = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, -150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <AnimatedBackground />
        
        {/* Hero content */}
        <motion.div 
          style={{ y, opacity }}
          className="container mx-auto px-4 relative z-10 text-center"
        >
          {/* Animated badge */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-gold/20 to-amber-400/20 rounded-full px-6 py-3 mb-8 border border-gold/30 backdrop-blur-sm"
          >
            <Sparkles className="w-5 h-5 text-gold animate-pulse" />
            <span className="text-gold font-bold text-sm uppercase tracking-wider">Get In Touch</span>
            <Sparkles className="w-5 h-5 text-gold animate-pulse" />
          </motion.div>

          {/* Main heading with staggered animation */}
          <motion.div className="mb-8">
            <motion.h1 
              className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold animate-gradient">
                Let's Connect.
              </span>
              <motion.span 
                className="block text-white mt-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Start Your Journey.
              </motion.span>
            </motion.h1>
          </motion.div>

          {/* Subtitle */}
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Ready to transform your PMO? Let's discuss your challenges and design solutions that deliver real results.
          </motion.p>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 inset-x-0 flex justify-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col items-center gap-2 text-gold">
            <span className="text-sm uppercase tracking-wider">Scroll to explore</span>
            <ChevronDown className="w-6 h-6" />
          </div>
        </motion.div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <motion.div 
              className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl p-8 rounded-2xl border border-gold/20 shadow-2xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              whileHover={{ boxShadow: "0 25px 50px rgba(212, 175, 55, 0.2)" }}
            >
              <motion.h2 
                className="text-3xl md:text-4xl font-bold text-center mb-8"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  Contact Us
                </span>
              </motion.h2>
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ 
                  duration: 0.5,
                  staggerChildren: 0.1,
                  ease: [0.25, 0.1, 0.25, 1.0]
                }}
              >
                {/* Email */}
                <motion.div 
                  className="flex items-start"
                  variants={{
                    hidden: { opacity: 0, x: -20 },
                    visible: { opacity: 1, x: 0 }
                  }}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ x: 5, boxShadow: "0 4px 12px -2px rgba(0, 0, 0, 0.08)" }}
                >
                  <motion.div 
                    className="h-14 w-14 rounded-full bg-gradient-to-br from-gold to-amber-400 flex items-center justify-center mr-5 flex-shrink-0"
                    initial={{ scale: 0.8 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    whileHover={{ rotate: 360 }}
                  >
                    <Mail className="text-black" size={24} />
                  </motion.div>
                  <div className="text-left">
                    <h3 className="font-semibold mb-1 text-white">Email Us</h3>
                    <p className="text-gray-400 mb-1">For general inquiries:</p>
                    <a href="mailto:inquiries@optigoldpmo.com" className="text-gold hover:text-amber-300 transition-colors font-semibold">
                      inquiries@optigoldpmo.com
                    </a>
                  </div>
                </motion.div>
                
                {/* Business Address */}
                <motion.div 
                  className="flex items-start"
                  variants={{
                    hidden: { opacity: 0, x: 20 },
                    visible: { opacity: 1, x: 0 }
                  }}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ x: -5, boxShadow: "0 4px 12px -2px rgba(0, 0, 0, 0.08)" }}
                >
                  <motion.div 
                    className="h-14 w-14 rounded-full bg-gradient-to-br from-gold to-amber-400 flex items-center justify-center mr-5 flex-shrink-0"
                    initial={{ scale: 0.8 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    whileHover={{ rotate: 360 }}
                  >
                    <MapPin className="text-black" size={24} />
                  </motion.div>
                  <div className="text-left">
                    <h3 className="font-semibold mb-1 text-white">Our Office</h3>
                    <address className="text-gold not-italic">
                      <a href="https://www.google.com/maps/search/?api=1&query=8816+Manchester+Rd+Suite+142+St.+Louis+MO+63144" 
                         className="hover:text-amber-300 transition-colors font-semibold"
                         target="_blank" 
                         rel="noopener noreferrer">
                        8816 Manchester Rd., Suite 142<br />
                        St. Louis, MO 63144
                      </a>
                    </address>
                  </div>
                </motion.div>
                
                {/* Schedule */}
                <motion.div 
                  className="flex items-start"
                  variants={{
                    hidden: { opacity: 0, x: -20 },
                    visible: { opacity: 1, x: 0 }
                  }}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ x: 5, boxShadow: "0 4px 12px -2px rgba(0, 0, 0, 0.08)" }}
                >
                  <motion.div 
                    className="h-14 w-14 rounded-full bg-gradient-to-br from-gold to-amber-400 flex items-center justify-center mr-5 flex-shrink-0"
                    initial={{ scale: 0.8 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    whileHover={{ rotate: 360 }}
                  >
                    <Calendar className="text-black" size={24} />
                  </motion.div>
                  <div className="text-left">
                    <h3 className="font-semibold mb-1 text-white">Schedule A Discovery Call</h3>
                    <p className="text-gray-400 mb-1">Book a 30-minute discovery call:</p>
                    <a href="https://cal.com/noah.mccloud" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-amber-300 transition-colors font-semibold">
                      Book a time slot
                    </a>
                  </div>
                </motion.div>
                
                {/* LinkedIn */}
                <motion.div 
                  className="flex items-start"
                  variants={{
                    hidden: { opacity: 0, x: 20 },
                    visible: { opacity: 1, x: 0 }
                  }}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ x: -5, boxShadow: "0 4px 12px -2px rgba(0, 0, 0, 0.08)" }}
                >
                  <motion.div 
                    className="h-14 w-14 flex items-center justify-center mr-5 flex-shrink-0"
                    initial={{ scale: 0.8 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    <img 
                      src="/linkedin-official-round.svg" 
                      alt="LinkedIn" 
                      className="w-[48px] h-[48px]" 
                    />
                  </motion.div>
                  <div className="text-left">
                    <h3 className="font-semibold mb-1 text-white">Connect With Us</h3>
                    <p className="text-gray-400 mb-1">Follow us on LinkedIn:</p>
                    <a href="https://www.linkedin.com/company/optigold/" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-amber-300 transition-colors font-semibold">
                      OptiGold PMO
                    </a>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section (Accordion) */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative">
        <motion.div 
          className="max-w-4xl mx-auto px-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
        >
          <motion.h2 
            className="text-4xl md:text-5xl font-bold text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
              Frequently Asked Questions
            </span>
          </motion.h2>

          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-2xl border border-gold/20 p-4 md:p-6 shadow-2xl">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="border-gold/20">
                <AccordionTrigger className="text-white">What size organizations do you typically work with?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  From first PMO to advanced portfolio management. We partner with growing businesses building foundations and enterprises running complex portfolios. We have deep experience serving organizations from $500M to $37B in scale.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-gold/20">
                <AccordionTrigger className="text-white">What is the pricing model for your services?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  We offer a mix of value-based pricing, hourly rates, fixed-fee project engagements, and retainer-based support depending on your needs. Pricing is tailored to the scope, duration, and complexity of each engagement. Let's connect to explore the best fit for your organization.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-gold/20">
                <AccordionTrigger className="text-white">How do you ensure delivery success?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  We take a pragmatic, outcome-focused approach measured by quantitative metrics (e.g., on-time delivery rates, cost savings, % of projects aligned with strategic goals) and qualitative indicators (e.g., trust, confidence, perception of the PMO as a strategic partner). We work side by side with our clients to define the most important PMO value metrics to the organization.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="border-gold/20">
                <AccordionTrigger className="text-white">Do you work with specific sectors or industries?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  While we bring experience in transportation and mobility, healthcare and telecommunications, we offer a proven approach that is adaptable across various sectors or industries. By prioritizing the alignment of PMO strategy with business value, we can customize solutions to meet your unique needs regardless or sector or industry.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5" className="border-gold/20">
                <AccordionTrigger className="text-white">What makes your PMO transformation approach different?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  Our approach leverages the OptiGold PMO Framework, which combines strategic insight and tactical execution with real-world experience and industry-standard best practices. We don't rely on one-size-fits-all solutions, instead, we tailor our work to your context, goals, and constraints. Grounded in both proven theory and practical application, we balance structure, agility, and pragmatism to help your PMO evolve and deliver measurable, lasting value to the business.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-6" className="border-gold/20">
                <AccordionTrigger className="text-white">How do we start?</AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  Begin with a short discovery call to confirm goals, scope, and fit. From there you receive a proposal with clear outcomes, cadence, and next steps.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default ContactPage;
